import { useMemo } from "react"
import { useDataStore } from "../../Data"
import type { DataStore } from "../../Data/Store"
import {
  DayApi,
  INoteAttachmentResponse,
  INoteCreateRequest,
  INoteParentType,
  INoteResponse,
  INoteUpdateRequest,
  NoteApi,
} from "../../Types"
import type { IDayResponse } from "../../Types/Generated/LogbookModel/Day"

export interface NoteWriterIds {
  shipId: number;
}

export interface NoteWriters {
  createDay: (tripId: number, day: number) => Promise<IDayResponse>;
  createNote: (parentType: INoteParentType, parentId: number, body: INoteCreateRequest) => Promise<INoteResponse>;
  updateNote: (noteId: number, values: INoteUpdateRequest) => Promise<INoteResponse>;
  uploadAttachment: (noteId: number, attachment: File) => Promise<INoteAttachmentResponse>;
  deleteAttachment: (noteId: number, attachmentId: number) => Promise<INoteAttachmentResponse>;
}

function createNoteRequest(
  api: NoteApi,
  parentType: INoteParentType,
  shipId: number,
  parentId: number,
  body: INoteCreateRequest,
): Promise<INoteResponse> {
  switch (parentType) {
    case INoteParentType.cruise:
      return api.notes.createForCruise({ params: { shipId, cruiseId: parentId }, body }).call();
    case INoteParentType.day:
      return api.notes.createForDay({ params: { shipId, dayId: parentId }, body }).call();
    case INoteParentType.trip:
      return api.notes.createForTrip({ params: { shipId, tripId: parentId }, body }).call();
    default:
      return Promise.reject(new Error(`Unknown note parent type: ${parentType}`));
  }
}

export function createStoreNoteWriters(store: DataStore, { shipId }: NoteWriterIds): NoteWriters {
  return {
    createDay: (tripId, day) => store.mutate({
      request: (client) =>
        new DayApi(client).days.create({ params: { shipId, tripId }, body: { day } }).call(),
      reconcile: (response) => ({ entities: { Day: [response.day] } }),
    }),
    createNote: (parentType, parentId, body) => store.mutate({
      request: (client) =>
        createNoteRequest(new NoteApi(client), parentType, shipId, parentId, body),
      reconcile: (response) => ({ entities: { Note: response.note ? [response.note] : [] } }),
    }),
    updateNote: (noteId, values) => store.mutate({
      request: (client) =>
        new NoteApi(client).notes.update({ params: { shipId, noteId }, body: values }).call(),
      reconcile: (response) => ({ entities: { Note: response.note ? [response.note] : [] } }),
    }),
    uploadAttachment: (noteId, attachment) => store.mutate({
      request: (client) =>
        new NoteApi(client).noteAttachments.create({ params: { shipId, noteId }, body: { attachment } }).call(),
      reconcile: (response) => ({
        entities: { NoteAttachment: response.attachment ? [response.attachment] : [] },
      }),
    }),
    deleteAttachment: (noteId, attachmentId) => store.mutate({
      optimistic: { remove: { NoteAttachment: [attachmentId] } },
      request: (client) =>
        new NoteApi(client).noteAttachments.delete({ params: { shipId, noteId, attachmentId } }).call(),
    }),
  };
}

export function useNoteWriters(shipId: number): NoteWriters {
  const store = useDataStore();
  return useMemo(() => createStoreNoteWriters(store, { shipId }), [shipId, store]);
}
